import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

interface ChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
  receiveTime?: number;
  sendTime?: number;
  messageId?: string;
  documents?: any[];
  message_context?: unknown;
  session_context?: unknown;
  [key: string]: unknown;
}

export interface TaskResultAttachment {
  id: string;
  taskName: string;
  taskType: string;
  timestamp: string;
  content: string;
  summary?: string;
  documentId?: string;
}

interface SessionState {
  // Session metadata
  sessionId: string;
  sessionName: string;
  sessionHasBeenSaved: boolean;
  savedFolderForSession: string | null;
  sessionIsShared: boolean;

  // Chat state
  chatMessages: ChatMessage[];
  chatInput: string;
  taskAttachments: TaskResultAttachment[];
  sentTaskResults: Record<string, TaskResultAttachment>; // Store sent task results by ID

  // Session documents
  sessionDocuments: unknown[];
  sessionResources: unknown[];
  sessionApplications: any[];

  // Actions
  setSessionId: (id: string) => void;
  setSessionName: (name: string) => void;
  setSessionHasBeenSaved: (saved: boolean) => void;
  setSavedFolderForSession: (folder: string | null) => void;
  setSessionIsShared: (shared: boolean) => void;

  setChatMessages: (messages: ChatMessage[]) => void;
  addChatMessage: (message: ChatMessage) => void;
  deleteChatMessage: (messageId: number) => void;
  setChatInput: (input: string) => void;

  addTaskAttachment: (attachment: TaskResultAttachment) => void;
  removeTaskAttachment: (attachmentId: string) => void;
  clearTaskAttachments: () => void;
  storeSentTaskResults: (attachments: TaskResultAttachment[]) => void;

  setSessionDocuments: (docs: unknown[]) => void;
  setSessionResources: (resources: unknown[]) => void;
  setSessionApplications: (apps: unknown[]) => void;

  // Reset session
  resetSession: () => void;
}

// Welcome message that appears as the first message
const WELCOME_MESSAGE: ChatMessage = {
  role: 'assistant',
  content: 'Welcome to Patent Loop! I can help you analyze patents, compare technical documents, review patent regulations, and examine prosecution histories. What would you like to explore?',
  receiveTime: Date.now(),
};

const initialState = {
  sessionId: '',
  sessionName: '',
  sessionHasBeenSaved: false,
  savedFolderForSession: null,
  sessionIsShared: false,
  chatMessages: [WELCOME_MESSAGE],
  chatInput: '',
  taskAttachments: [],
  sentTaskResults: {},
  sessionDocuments: [],
  sessionResources: [],
  sessionApplications: [],
};

export const useSessionStore = create<SessionState>()(
  devtools(
    persist(
      (set) => ({
        ...initialState,

        setSessionId: (id) => set({ sessionId: id }),
        setSessionName: (name) => set({ sessionName: name }),
        setSessionHasBeenSaved: (saved) => set({ sessionHasBeenSaved: saved }),
        setSavedFolderForSession: (folder) => set({ savedFolderForSession: folder }),
        setSessionIsShared: (shared) => set({ sessionIsShared: shared }),

        setChatMessages: (messages) => set({ chatMessages: messages }),
        addChatMessage: (message) => set((state) => ({
          chatMessages: [...state.chatMessages, message]
        })),
        deleteChatMessage: (messageId) => set((state) => ({
          chatMessages: state.chatMessages.filter((_, index) => index !== messageId),
        })),
        setChatInput: (input) => set({ chatInput: input }),

        addTaskAttachment: (attachment) => set((state) => ({
          taskAttachments: [...state.taskAttachments, attachment]
        })),
        removeTaskAttachment: (attachmentId) => set((state) => ({
          taskAttachments: state.taskAttachments.filter((a) => a.id !== attachmentId)
        })),
        clearTaskAttachments: () => set({ taskAttachments: [] }),
        storeSentTaskResults: (attachments) => set((state) => {
          const newSentResults = { ...state.sentTaskResults };
          attachments.forEach(attachment => {
            newSentResults[attachment.id] = attachment;
          });
          return { sentTaskResults: newSentResults };
        }),

        setSessionDocuments: (docs) => set({
          sessionDocuments: docs,
        }),
        setSessionResources: (resources) => set({
          sessionResources: resources,
        }),
        setSessionApplications: (apps) => set({
          sessionApplications: apps,
        }),

        resetSession: () => set({
          ...initialState,
          chatMessages: [{
            ...WELCOME_MESSAGE,
            receiveTime: Date.now(), // Fresh timestamp for new session
          }],
        }),
      }),
      {
        name: 'session-storage',
        partialize: (state) => ({
          chatMessages: state.chatMessages,
          taskAttachments: state.taskAttachments,
          sentTaskResults: state.sentTaskResults,
          sessionId: state.sessionId,
          sessionName: state.sessionName,
          sessionHasBeenSaved: state.sessionHasBeenSaved,
          savedFolderForSession: state.savedFolderForSession,
        }),
        onRehydrateStorage: () => (state) => {
          // Ensure welcome message is present if chat is empty
          if (state && (!state.chatMessages || state.chatMessages.length === 0)) {
            state.chatMessages = [WELCOME_MESSAGE];
          }

          // If there's a sessionId, it means this session has been saved
          if (state && state.sessionId) {
            state.sessionHasBeenSaved = true;
          }
        },
      }
    )
  )
);
